import {
    Box,
    Button,
    CircularProgress,
    Paper,
    TextField,
    Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { axiosInstance } from '../../helpers/axios'
import { formatUsPhoneInput } from '../../helpers/stringHelpers'
import { useAppDispatch } from '../../hooks'
import { RootState } from '../../store'
import { setRootUser } from '../../store/User/action'
import { PortalAccount } from '../../types/portalAccount'
import { PasswordRequirements } from '../Login/ForgotPassword/PasswordRequirements'
import { passwordValidator } from '../Login/ForgotPassword/passwordValidator'

const darkFieldSx = {
    '& .MuiInputLabel-root': { color: '#8b949e' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#46b083' },
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(139, 148, 158, 0.4)' },
        '&:hover fieldset': { borderColor: 'rgba(139, 148, 158, 0.7)' },
        '&.Mui-focused fieldset': { borderColor: '#46b083' },
        '&.Mui-disabled': {
            color: '#8b949e',
            '& fieldset': { borderColor: 'rgba(139, 148, 158, 0.25)' },
        },
    },
    '& .MuiFormHelperText-root': { color: '#8b949e' },
} as const

const getApiError = (err: unknown, fallback: string) => {
    const axiosError = err as {
        response?: { data?: { error?: string; message?: string } }
    }
    return (
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        fallback
    )
}

const paperSx = {
    background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
    borderRadius: 3,
    p: 3,
    mb: 3,
    border: '1px solid rgba(139, 148, 158, 0.25)',
    width: '100%',
    maxWidth: 560,
} as const

export const Account = () => {
    const dispatch = useAppDispatch()
    const rootUser = useSelector((state: RootState) => state.user.rootUser)

    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [contactError, setContactError] = useState('')
    const [contactSaved, setContactSaved] = useState(false)

    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordSaving, setPasswordSaving] = useState(false)
    const [passwordError, setPasswordError] = useState('')
    const [passwordSaved, setPasswordSaved] = useState(false)

    const passwordValidResult = passwordValidator(newPassword, confirmPassword)

    useEffect(() => {
        let cancelled = false
        axiosInstance
            .get<PortalAccount>('user/account/')
            .then((res) => {
                if (cancelled) return
                setName(res.data.name || '')
                setEmail(res.data.email || '')
                setPhone(formatUsPhoneInput(res.data.cell_phone || ''))
            })
            .catch((err) => {
                if (cancelled) return
                setContactError(getApiError(err, 'Unable to load your account.'))
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [])

    const handleSaveContact = async () => {
        if (!name.trim()) {
            setContactError('Preferred name is required.')
            return
        }
        setSaving(true)
        setContactError('')
        setContactSaved(false)
        try {
            const res = await axiosInstance.patch<PortalAccount>('user/account/', {
                name: name.trim(),
                cell_phone: phone,
            })
            setName(res.data.name || '')
            setEmail(res.data.email || '')
            setPhone(formatUsPhoneInput(res.data.cell_phone || ''))
            setContactSaved(true)
            if (rootUser) {
                dispatch(
                    setRootUser({
                        ...rootUser,
                        name: res.data.name,
                        cell_phone: res.data.cell_phone,
                    }),
                )
            }
        } catch (err) {
            setContactError(getApiError(err, 'Unable to save your account.'))
        } finally {
            setSaving(false)
        }
    }

    const handleChangePassword = async () => {
        if (!passwordValidResult.passwordValid || !currentPassword) {
            return
        }
        setPasswordSaving(true)
        setPasswordError('')
        setPasswordSaved(false)
        try {
            await axiosInstance.post('user/account/password/', {
                current_password: currentPassword,
                new_password: newPassword,
            })
            setCurrentPassword('')
            setNewPassword('')
            setConfirmPassword('')
            setPasswordSaved(true)
        } catch (err) {
            setPasswordError(
                getApiError(err, 'Unable to change your password.'),
            )
        } finally {
            setPasswordSaving(false)
        }
    }

    if (loading) {
        return (
            <ResponsivePageContainer
                title="Account"
                subtitle="Your preferred name, mobile number, and password."
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            </ResponsivePageContainer>
        )
    }

    return (
        <ResponsivePageContainer
            title="Account"
            subtitle="Your preferred name, mobile number, and password."
        >
            <Paper elevation={0} sx={paperSx}>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                    Contact
                </Typography>
                <TextField
                    fullWidth
                    label="Preferred name"
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value)
                        setContactSaved(false)
                    }}
                    helperText="How we greet you. This is not your legal name on the application."
                    sx={{ ...darkFieldSx, mb: 2 }}
                />
                <TextField
                    fullWidth
                    label="Email"
                    value={email}
                    disabled
                    helperText="Used to sign in. Contact the office if you need this changed."
                    sx={{ ...darkFieldSx, mb: 2 }}
                />
                <TextField
                    fullWidth
                    label="Mobile phone"
                    type="tel"
                    value={phone}
                    onChange={(event) => {
                        setPhone(formatUsPhoneInput(event.target.value))
                        setContactSaved(false)
                    }}
                    helperText="Used for calls and texts from the property."
                    sx={{ ...darkFieldSx, mb: 2 }}
                />
                {contactError && (
                    <Typography variant="body2" sx={{ color: '#f87171', mb: 2 }}>
                        {contactError}
                    </Typography>
                )}
                {contactSaved && (
                    <Typography variant="body2" sx={{ color: '#46b083', mb: 2 }}>
                        Contact saved.
                    </Typography>
                )}
                <Button
                    variant="contained"
                    onClick={handleSaveContact}
                    disabled={saving || !name.trim()}
                    sx={{
                        backgroundColor: '#008C85',
                        '&:hover': { backgroundColor: '#007c75' },
                    }}
                >
                    {saving ? 'Saving...' : 'Save contact'}
                </Button>
            </Paper>

            <Paper elevation={0} sx={paperSx}>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                    Password
                </Typography>
                <TextField
                    fullWidth
                    type="password"
                    label="Current password"
                    value={currentPassword}
                    onChange={(event) => {
                        setCurrentPassword(event.target.value)
                        setPasswordSaved(false)
                    }}
                    sx={{ ...darkFieldSx, mb: 2 }}
                />
                <TextField
                    fullWidth
                    type="password"
                    label="New password"
                    value={newPassword}
                    onChange={(event) => {
                        setNewPassword(event.target.value)
                        setPasswordSaved(false)
                    }}
                    sx={{ ...darkFieldSx, mb: 2 }}
                />
                <TextField
                    fullWidth
                    type="password"
                    label="Confirm new password"
                    value={confirmPassword}
                    onChange={(event) => {
                        setConfirmPassword(event.target.value)
                        setPasswordSaved(false)
                    }}
                    sx={{ ...darkFieldSx, mb: 2 }}
                />
                <Box sx={{ color: '#8b949e', mb: 2 }}>
                    <PasswordRequirements
                        passwordRequirementResult={passwordValidResult}
                    />
                </Box>
                {passwordError && (
                    <Typography variant="body2" sx={{ color: '#f87171', mb: 2 }}>
                        {passwordError}
                    </Typography>
                )}
                {passwordSaved && (
                    <Typography variant="body2" sx={{ color: '#46b083', mb: 2 }}>
                        Password updated.
                    </Typography>
                )}
                <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    disabled={
                        passwordSaving ||
                        !currentPassword ||
                        !passwordValidResult.passwordValid
                    }
                    sx={{
                        backgroundColor: '#008C85',
                        '&:hover': { backgroundColor: '#007c75' },
                    }}
                >
                    {passwordSaving ? 'Updating...' : 'Update password'}
                </Button>
            </Paper>
        </ResponsivePageContainer>
    )
}
